import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Chart as ChartJS, ChartType } from 'chart.js';

export interface UseChartActionsResult<TType extends ChartType = 'line'> {
    chartRef: React.RefObject<ChartJS<TType> | null>;
    isFullScreen: boolean;
    handleFullScreenToggle: () => void;
    fullScreenContainerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Shared hook to wire a Chart.js chart with imperative actions:
 * - `chartRef` to give to the chart for download/zoom-reset operations.
 * - `fullScreenContainerRef` to attach to the chart's outer container.
 * - `isFullScreen` + `handleFullScreenToggle` to manage fullscreen mode.
 *
 * Used by chart-driven views across the app (behavior, health, compare, …).
 *
 * Generic over the Chart.js chart type so bar charts get a correctly typed
 * `chartRef`. Defaults to `'line'`, so existing callers are unaffected.
 */
export function useChartActions<TType extends ChartType = 'line'>(): UseChartActionsResult<TType> {
    const chartRef = useRef<ChartJS<TType>>(null);
    const fullScreenContainerRef = useRef<HTMLDivElement>(null);
    const [isFullScreen, setIsFullScreen] = useState(false);

    useEffect(() => {
        const handleFullScreenChange = () => {
            const isCurrentlyFullScreen =
                document.fullscreenElement === fullScreenContainerRef.current;
            setIsFullScreen(isCurrentlyFullScreen);
            chartRef.current?.resize();
        };

        document.addEventListener('fullscreenchange', handleFullScreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
        };
    }, []);

    const handleFullScreenToggle = useCallback(() => {
        const containerToFullscreen = fullScreenContainerRef.current;

        if (!containerToFullscreen) {
            return;
        }

        // A browser may refuse (no user gesture, an iframe without permission); the chart then
        // stays as it is, which is all there is to show for it.
        const ignoreRefusal = (): void => {};
        if (isFullScreen) {
            document.exitFullscreen().catch(ignoreRefusal);
        } else {
            containerToFullscreen.requestFullscreen().catch(ignoreRefusal);
        }
    }, [isFullScreen]);

    return {
        chartRef,
        isFullScreen,
        handleFullScreenToggle,
        fullScreenContainerRef,
    };
}
